import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Loader2, AlertTriangle } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Client-facing watermark bar height (buttonless chrome).
const BAR_H = 40;

/**
 * Public client preview - reached only via a per-workspace secret token
 * (/share/:token). Shows ONE workspace's mockup pages under a slim
 * Web Marketing Machine watermark bar with NO buttons or navigation chrome.
 * Clients browse using the mockup's own menus; internal links are
 * intercepted and swapped in-place.
 */
export default function SharePreviewPage() {
  const { token } = useParams();
  const [meta, setMeta] = useState(null);
  const [metaError, setMetaError] = useState(false);
  const [slug, setSlug] = useState(null);
  const [page, setPage] = useState(null);
  const [pageLoading, setPageLoading] = useState(true);
  const [pageError, setPageError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/share/${token}/site`)
      .then(({ data }) => {
        if (cancelled) return;
        setMeta(data);
        setSlug(data.pages?.[0]?.slug || null);
        if (data.site_name) document.title = `${data.site_name} — Site Preview`;
      })
      .catch(() => { if (!cancelled) setMetaError(true); });
    return () => { cancelled = true; };
  }, [token]);

  const loadPage = useCallback(async () => {
    if (!slug) return;
    setPageLoading(true);
    setPageError(false);
    try {
      const { data } = await axios.get(`${API}/share/${token}/page/${slug}`);
      setPage(data);
      window.scrollTo({ top: 0 });
    } catch (e) {
      setPageError(true);
      setPage(null);
    } finally {
      setPageLoading(false);
    }
  }, [token, slug]);

  useEffect(() => { loadPage(); }, [loadPage]);

  const slugSet = useMemo(() => new Set((meta?.pages || []).map((p) => p.slug)), [meta]);

  // Same in-mockup navigation behavior as the internal viewer: internal links
  // swap pages, external/tel/mailto behave naturally, unknown paths no-op.
  const onCanvasClick = (e) => {
    const a = e.target.closest("a");
    if (!a) return;
    const href = a.getAttribute("href") || "";
    if (href.startsWith("tel:") || href.startsWith("mailto:") || href.startsWith("#")) return;
    e.preventDefault();
    if (/^https?:\/\//i.test(href)) {
      window.open(href, "_blank", "noopener");
      return;
    }
    const target = href.replace(/^\/+|\/+$/g, "") || "home";
    if (slugSet.has(target)) setSlug(target);
  };

  return (
    <div data-testid="share-preview-page" className="min-h-screen bg-white">
      {/* Watermark bar: branding only - deliberately zero buttons/links */}
      <div
        data-testid="share-watermark-bar"
        className="fixed top-0 left-0 right-0 z-[100] flex items-center gap-2 border-b border-black/10 bg-[#f2f2f3] px-4 select-none"
        style={{ height: BAR_H, fontFamily: '"IBM Plex Sans", ui-sans-serif, sans-serif' }}
      >
        <img src="/branding/wmm-logo-full.png" alt="" className="h-5 w-5 shrink-0" draggable="false" />
        <span className="text-[11px] font-semibold text-[#0050c0] whitespace-nowrap">Web Marketing Machine</span>
        <span className="h-3 w-px bg-black/15" />
        <span data-testid="share-site-name" className="truncate text-[10px] font-semibold uppercase tracking-[0.08em] text-[#8a8a8a]">
          {meta ? `${meta.site_name} — Site Preview` : "Site Preview"}
        </span>
      </div>

      <div style={{ paddingTop: BAR_H }}>
        {metaError ? (
          <div data-testid="share-error-state" className="flex flex-col items-center gap-3 px-6 py-28 text-center">
            <AlertTriangle size={30} className="text-amber-500" />
            <p className="text-[16px] font-bold text-[#383838]">This preview link isn&apos;t active</p>
            <p className="max-w-md text-[13.5px] text-[#6e6e6e]">
              Please check the link you received, or contact your Web Marketing Machine representative for a fresh one.
            </p>
          </div>
        ) : !meta || (pageLoading && !page) ? (
          <div className="space-y-4 p-6" data-testid="share-loading-state">
            <Skeleton className="h-72 rounded-[10px]" />
            <Skeleton className="h-8 w-2/3 rounded-[8px]" />
            <Skeleton className="h-8 w-1/2 rounded-[8px]" />
            <Skeleton className="h-44 rounded-[10px]" />
          </div>
        ) : pageError ? (
          <div data-testid="share-page-error" className="flex flex-col items-center gap-3 px-6 py-28 text-center">
            <AlertTriangle size={30} className="text-amber-500" />
            <p className="text-[16px] font-bold text-[#383838]">Couldn&apos;t load this page</p>
            <p className="max-w-md text-[13.5px] text-[#6e6e6e]">Refresh your browser to try again.</p>
          </div>
        ) : page ? (
          <div
            onClick={onCanvasClick}
            className="pv-canvas"
            data-testid="share-canvas"
            style={{ "--pv-chrome-top": `${BAR_H}px` }}
            dangerouslySetInnerHTML={{ __html: page.html }}
          />
        ) : (
          <div data-testid="share-empty-state" className="flex flex-col items-center gap-3 px-6 py-28 text-center">
            <Loader2 size={22} className="animate-spin text-[#0050c0]" />
          </div>
        )}
      </div>
    </div>
  );
}
